import SectionIcon from '@components/SectionIcon';
import {
  Badge,
  NewBox as Box,
  Button,
  Flex,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Progress,
  Separator,
  Text,
} from '@retracedgmbh/ui-components-lib';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import useTranslator from '@/hooks/useTranslator';
import type { AiProcessingItem } from '@/services/api/types/ai-processing';

// Static asset-type → label key maps. Explicit literals (never a dynamic
// `jobType.${assetType}` key) so the strings are statically analysable.
// In-progress rows show the running operation; ready rows show what's ready to
// review (different wording per the design).
const JOB_TYPE_LABEL_KEYS: Record<string, string> = {
  AUDIT: 'popover.jobType.audit',
  CERTIFICATE: 'popover.jobType.certificate',
  COMPANY_DOCUMENTS: 'popover.jobType.companyDocuments',
  TRACING: 'popover.jobType.tracing',
  CERTIFIED_MATERIALS: 'popover.jobType.certifiedMaterials',
};

const READY_TYPE_LABEL_KEYS: Record<string, string> = {
  AUDIT: 'popover.readyType.audit',
  CERTIFICATE: 'popover.readyType.certificate',
  COMPANY_DOCUMENTS: 'popover.readyType.companyDocuments',
  TRACING: 'popover.readyType.tracing',
  CERTIFIED_MATERIALS: 'popover.readyType.certifiedMaterials',
};

// Audit jobs don't set `assetName` yet (the /capa/process upsert only stores
// fileId/workflowId), so fall back to a `metadata.file.name` if present —
// mirrors the sheet card's filename fallback. May be empty for audits until the
// backend populates a name (see AI_PROCESSING_QUEUE.md flag).
function readFileName(item: AiProcessingItem): string {
  if (item.assetName) return item.assetName;
  const file = (item.metadata as Record<string, unknown> | null)?.file as
    | { name?: string }
    | undefined;
  return file?.name ?? '';
}

interface ActiveJobsPopoverProps {
  jobsTotal: number;
  runningCount: number;
  readyCount: number;
  inProgressItems: AiProcessingItem[];
  readyItems: AiProcessingItem[];
  onOpenQueue: () => void;
  children: ReactNode;
}

export default function ActiveJobsPopover({
  jobsTotal,
  runningCount,
  readyCount,
  inProgressItems,
  readyItems,
  onOpenQueue,
  children,
}: ActiveJobsPopoverProps) {
  const { getTranslation } = useTranslator({ page: 'ai-processing' });
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const show = () => {
    cancelClose();
    setOpen(true);
  };
  // Grace delay so moving the pointer from the button onto the popover content
  // doesn't dismiss it.
  const scheduleHide = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  useEffect(() => cancelClose, []);

  const summaryLine = [
    getTranslation('popover.jobsCount', { count: jobsTotal }),
    getTranslation('popover.running', { count: runningCount }),
    getTranslation('popover.ready', { count: readyCount }),
  ].join(' · ');

  const handleOpenQueue = () => {
    setOpen(false);
    onOpenQueue();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        {/* Anchor only (no click-to-toggle) — the button keeps its own onClick
            to open the full queue; hover drives the preview. */}
        <Box
          className='tw:inline-block'
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
        >
          {children}
        </Box>
      </PopoverAnchor>
      <PopoverContent
        align='end'
        side='bottom'
        sideOffset={8}
        onOpenAutoFocus={e => e.preventDefault()}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        className='tw:w-[340px] tw:p-0'
      >
        <Flex direction='column' padding='xl' gap='lg'>
          {/* Header */}
          <Flex direction='column' gap='xxs'>
            <Flex justify='between' alignItems='center' gap='sm'>
              <Text size='base' weight='semibold'>
                {getTranslation('popover.title')}
              </Text>
              {readyCount > 0 && (
                <Badge type='warning' radius='squared'>
                  <Text className='tw:text-amber-800'>
                    {getTranslation('stat.toReview', { count: readyCount })}
                  </Text>
                </Badge>
              )}
            </Flex>
            <Text size='small' color='secondary'>
              {summaryLine}
            </Text>
          </Flex>

          {/* In-progress jobs */}
          {inProgressItems.length > 0 && (
            <>
              <Separator />
              <Flex direction='column' gap='md'>
                <Text
                  color='secondary'
                  weight='semibold'
                  className='tw:uppercase tw:tracking-wide tw:text-xs'
                >
                  {getTranslation('popover.inProgress')}
                </Text>
                {/* Show ~2 in-progress rows before scrolling (each row is a
                    filename + progress bar); the rest scroll. A partial next
                    row peeks under the cap to hint there's more. */}
                <Flex
                  direction='column'
                  gap='lg'
                  className='tw:max-h-[140px] tw:overflow-y-auto'
                >
                  {inProgressItems.map(item => {
                    const fileName = readFileName(item);
                    const labelKey = JOB_TYPE_LABEL_KEYS[item.assetType];
                    const jobLabel = labelKey
                      ? getTranslation(labelKey)
                      : item.assetType;
                    const pct = item.percentage ?? 0;
                    return (
                      <Flex key={item.id} direction='column' gap='sm'>
                        <Flex gap='md' alignItems='center'>
                          <SectionIcon
                            icon='fa-regular fa-file'
                            type='primary'
                          />
                          <Flex
                            direction='column'
                            gap='xxs'
                            className='tw:min-w-0'
                          >
                            <Text
                              size='small'
                              weight='semibold'
                              textOverFlow='truncate'
                            >
                              {fileName}
                            </Text>
                            <Text
                              color='secondary'
                              className='tw:uppercase tw:tracking-wide tw:text-[10px] tw:leading-none'
                            >
                              {jobLabel}
                            </Text>
                          </Flex>
                        </Flex>
                        <Flex alignItems='center' gap='sm'>
                          <Progress thin value={pct} className='tw:flex-1' />
                          <Text
                            size='small'
                            color='secondary'
                            className='tw:whitespace-nowrap'
                          >
                            {getTranslation('popover.percentComplete', {
                              percentage: pct,
                            })}
                          </Text>
                        </Flex>
                      </Flex>
                    );
                  })}
                </Flex>
              </Flex>
            </>
          )}

          {/* Ready-to-review jobs */}
          {readyItems.length > 0 && (
            <>
              <Separator />
              <Flex direction='column' gap='md'>
                <Text
                  color='secondary'
                  weight='semibold'
                  className='tw:uppercase tw:tracking-wide tw:text-xs'
                >
                  {getTranslation('popover.readyToReview')}
                </Text>
                {/* Show ~3 ready-to-review rows before scrolling (single-line
                    rows); the rest scroll, with a partial next row peeking. */}
                <Flex
                  direction='column'
                  gap='lg'
                  className='tw:max-h-[132px] tw:overflow-y-auto'
                >
                  {readyItems.map(item => {
                    const fileName = readFileName(item);
                    const labelKey = READY_TYPE_LABEL_KEYS[item.assetType];
                    const readyLabel = labelKey
                      ? getTranslation(labelKey)
                      : item.assetType;
                    return (
                      <Flex key={item.id} gap='md' alignItems='center'>
                        <SectionIcon
                          icon='fa-regular fa-circle-check'
                          type='warning'
                        />
                        <Flex
                          direction='column'
                          gap='xxs'
                          className='tw:flex-1 tw:min-w-0'
                        >
                          <Text
                            size='small'
                            weight='semibold'
                            textOverFlow='truncate'
                          >
                            {fileName}
                          </Text>
                          <Text
                            color='secondary'
                            className='tw:uppercase tw:tracking-wide tw:text-[10px] tw:leading-none'
                          >
                            {readyLabel}
                          </Text>
                        </Flex>
                        <Badge type='warning' radius='squared'>
                          <Text className='tw:text-amber-800'>
                            {getTranslation('popover.readyToReview')}
                          </Text>
                        </Badge>
                      </Flex>
                    );
                  })}
                </Flex>
              </Flex>
            </>
          )}

          <Separator />

          {/* Footer */}
          <Flex justify='end'>
            <Button
              onClick={handleOpenQueue}
              icon='fa-regular fa-chevron-right'
              iconPosition='right'
            >
              {getTranslation('popover.openQueue')}
            </Button>
          </Flex>
        </Flex>
      </PopoverContent>
    </Popover>
  );
}
